import { CalendlyExpert } from "../../features/experts/types/CalendlyExpert";
import { ExpertRole } from "../../features/experts/types/ExpertRole";
import { BASE_URL, requestOptionsGET } from "../Constants";

/**
 * Fetches a list of medical users based on their role.
 * 
 * This function sends a request to the API to retrieve a list of medical users 
 * who have the specified role (e.g., Nutritionist, Specialist).
 * If no users are found for the given role, `null` is returned.
 * 
 * @param idToken - The authentication token to authorize the request.
 * @param role - The role of the medical user to search for (e.g., `ExpertRole.Nutritionist`).
 * 
 * @returns A Promise that resolves to an array of `CalendlyExpert` objects if users are found, 
 *          or `null` if no users are found for the given role.
 * 
 * @throws Error if there is an issue with the API request or response.
 */
export async function getMedicalUserByRole(
    idToken: string,
    role: ExpertRole,
): Promise<CalendlyExpert[] | null> {
    try {
        const response = await fetch(`${BASE_URL}/api/medical-user/role/${role}`, requestOptionsGET(idToken));

        if (!response.ok) {
            if (response.status === 404) {
                console.warn(`No medical user found for role: ${role}`);
                return null;
            }
            throw new Error(`Errore nel trovare il medico: ${role} - ${response.statusText}`);
        }

        const medicalUsers: CalendlyExpert[] = await response.json();
        return medicalUsers.length > 0 ? medicalUsers : null;

    } catch (error) {
        console.error("Get medical user by role failed:", error);
        throw error; // Rethrow the original error for better debugging
    }
}

export async function getNutritionistByPatientId(
    idToken: string,
    userId: string,
): Promise<CalendlyExpert | null> {
    try {
        const response = await fetch(`${BASE_URL}/api/medical-user/patient/${userId}`, requestOptionsGET(idToken))
        if (response.status === 404) {
            return null;
        }
        if (!response.ok) {
            throw new Error(`Failed to fetch nutritionist. Status: ${response.status}`);
        }
        const nutritionist: CalendlyExpert = await response.json();
        if (nutritionist) {
            return nutritionist;
        } else {
            throw new Error('Nutritionist data is empty');
        }
    } catch (error) {
        console.error("Get nutritionist by patient id failed:", error);
        return null;
    }
}

export async function getPhysicianByPatientId(
    idToken: string,
    userId: string,
): Promise<CalendlyExpert | null> {
    try {
        const response = await fetch(
            `${BASE_URL}/api/medical-user/patient/physician/${userId}`,
            requestOptionsGET(idToken)
        );

        if (response.status === 401) {
            throw new Error("Session expired");
        }

        if (response.status === 404) {
            return null;
        }
        if (!response.ok) {
            throw new Error(`Failed to fetch physician. Status: ${response.status}`);
        }
        const physician: CalendlyExpert = await response.json();
        if (physician) {
            return physician;
        } else {
            throw new Error('Physician data is empty');
        }
    } catch (error) {
        console.error("Get physician by patient id failed:", error);
        throw error;
    }
}

// ASSIGN specialist to a user
export async function assignMedicalUserToPatient(
    nutriId: string,
    userId: string,
    idToken: string,
): Promise<CalendlyExpert | null> {
    try {
        const response = await fetch(`${BASE_URL}/api/medical-user/assign/`, {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': idToken,
            },
            body: JSON.stringify(
                {
                    patient_id: userId,
                    medicaluser_id: nutriId,
                }
            )
        })
        const expert = await response.json()
        if (expert) {
            return expert
        } else {
            throw new Error('No expert assigned');
        }
    } catch (error) {
        console.error("Assign nutritionist to patient id failed:", error);
        return null;
    }
}